/**
 * Jurisdiction weights: how likely you are to see *any* plate from this issuer
 * while driving on a national average road. Higher weight = rarer = worth more.
 *
 * Every plate's point value is derived as `familyBase * jurisdictionWeight`, so
 * rebalancing an entire state is a one-number change here.
 */

export type Jurisdiction =
  | 'NSW' | 'VIC' | 'QLD' | 'SA' | 'WA' | 'TAS' | 'NT' | 'ACT'
  | 'CTH'  // Commonwealth / federal
  | 'EXT'; // External territories

export interface JurisdictionDef {
  code: Jurisdiction;
  /** Full name, used in UI headings and filters. */
  name: string;
  /** Short label for chips and filters. */
  short: string;
  weight: number;
  /** Default [background, text] colours for the current general issue. */
  colours: [string, string];
  /**
   * Default serial and legend for the mini plate pictogram. Individual specs
   * override these where the real plate looks different.
   */
  sample: string;
  legend: string;
}

export const JURISDICTIONS: Record<Jurisdiction, JurisdictionDef> = {
  NSW: {
    code: 'NSW', name: 'New South Wales', short: 'NSW', weight: 1.0,
    colours: ['#f2d200', '#111111'], sample: 'AB·12·CD', legend: 'NEW SOUTH WALES',
  },
  VIC: {
    code: 'VIC', name: 'Victoria', short: 'VIC', weight: 1.0,
    colours: ['#ffffff', '#1b3a6b'], sample: '1AB·2CD', legend: 'VICTORIA',
  },
  QLD: {
    code: 'QLD', name: 'Queensland', short: 'QLD', weight: 1.2,
    colours: ['#ffffff', '#6b1f3a'], sample: '123·AB4', legend: 'QUEENSLAND',
  },
  SA: {
    code: 'SA', name: 'South Australia', short: 'SA', weight: 2.0,
    colours: ['#ffffff', '#111111'], sample: 'S123·ABC', legend: 'SOUTH AUSTRALIA',
  },
  WA: {
    code: 'WA', name: 'Western Australia', short: 'WA', weight: 2.5,
    colours: ['#ffffff', '#1f5f8f'], sample: '1ABC·123', legend: 'WESTERN AUSTRALIA',
  },
  ACT: {
    code: 'ACT', name: 'Australian Capital Territory', short: 'ACT', weight: 3.0,
    colours: ['#ffffff', '#2b4c8c'], sample: 'YAB·12C', legend: 'CANBERRA',
  },
  TAS: {
    code: 'TAS', name: 'Tasmania', short: 'TAS', weight: 4.0,
    colours: ['#ffffff', '#1f4b8f'], sample: 'A·12·BC', legend: 'TASMANIA',
  },
  NT: {
    code: 'NT', name: 'Northern Territory', short: 'NT', weight: 6.0,
    colours: ['#ffffff', '#b5451b'], sample: 'CA·12·BC', legend: 'NT OUTBACK',
  },
  // Every CTH and EXT plate is family `oneOff` and sets its own points, so these
  // two weights are never consulted by `pointsFor` - see the note there. They
  // are kept honest, rather than removed, because they are shown in the audit
  // script and the contact sheet: EXT sat at 100 for a long time, which read as
  // a 100x multiplier doing work that it was not doing. These now say roughly
  // what the plates really score against a NSW plate of the same kind.
  CTH: {
    code: 'CTH', name: 'Commonwealth', short: 'CTH', weight: 5.0,
    colours: ['#ffffff', '#333333'], sample: 'Z·1234', legend: 'COMMONWEALTH',
  },
  EXT: {
    code: 'EXT', name: 'External Territories', short: 'EXT', weight: 8.0,
    colours: ['#ffffff', '#111111'], sample: '1234', legend: 'AUSTRALIA',
  },
};

export const JURISDICTION_ORDER: Jurisdiction[] = [
  'NSW', 'VIC', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT', 'CTH', 'EXT',
];
